import * as THREE from 'three';
import type {
  DayCycleConfig,
  MasterLightingConfig,
  StyleOverride,
  StyleOverrideMap,
} from './StyleConfig';
import { lerpHex } from './LightingCurves';

export function sampleDayCycle(dayCycle: DayCycleConfig): {
  master: MasterLightingConfig;
  overrides: StyleOverrideMap;
} | null {
  if (!dayCycle.enabled || dayCycle.keyframes.length === 0) return null;
  if (dayCycle.keyframes.length === 1) {
    const only = dayCycle.keyframes[0];
    return { master: clone(only.master), overrides: clone(only.overrides) };
  }

  const scrub = THREE.MathUtils.clamp(dayCycle.scrub, 0, 1);
  const scaled = scrub * (dayCycle.keyframes.length - 1);
  const leftIndex = Math.floor(scaled);
  const rightIndex = Math.min(dayCycle.keyframes.length - 1, leftIndex + 1);
  const t = rightIndex === leftIndex ? 0 : scaled - leftIndex;
  const left = dayCycle.keyframes[leftIndex];
  const right = dayCycle.keyframes[rightIndex];

  return {
    master: lerpMaster(left.master, right.master, t),
    overrides: lerpOverrides(left.overrides, right.overrides, t),
  };
}

function lerpMaster(
  left: MasterLightingConfig,
  right: MasterLightingConfig,
  t: number,
): MasterLightingConfig {
  return {
    sunElevationDeg: THREE.MathUtils.lerp(left.sunElevationDeg, right.sunElevationDeg, t),
    sunAzimuthDeg: lerpAngleDeg(left.sunAzimuthDeg, right.sunAzimuthDeg, t),
    secondarySun: {
      enabled: t < 0.5 ? left.secondarySun.enabled : right.secondarySun.enabled,
      relativeElevationDeg: THREE.MathUtils.lerp(
        left.secondarySun.relativeElevationDeg,
        right.secondarySun.relativeElevationDeg,
        t,
      ),
      relativeAzimuthDeg: lerpAngleDeg(
        left.secondarySun.relativeAzimuthDeg,
        right.secondarySun.relativeAzimuthDeg,
        t,
      ),
      intensity: THREE.MathUtils.lerp(left.secondarySun.intensity, right.secondarySun.intensity, t),
    },
    atmosphere: {
      turbidity: THREE.MathUtils.lerp(left.atmosphere.turbidity, right.atmosphere.turbidity, t),
      hazeBoost: THREE.MathUtils.lerp(left.atmosphere.hazeBoost, right.atmosphere.hazeBoost, t),
      warmthBias: THREE.MathUtils.lerp(left.atmosphere.warmthBias, right.atmosphere.warmthBias, t),
    },
  };
}

function lerpOverrides(
  left: StyleOverrideMap,
  right: StyleOverrideMap,
  t: number,
): StyleOverrideMap {
  const result: StyleOverrideMap = {};
  const paths = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const path of paths) {
    const a = left[path];
    const b = right[path];
    if (!a || !b) {
      result[path] = clone((a ?? b) as StyleOverride);
      continue;
    }
    result[path] = {
      enabled: t < 0.5 ? a.enabled : b.enabled,
      value: lerpOverrideValue(a.value, b.value, t),
    };
  }
  return result;
}

function lerpOverrideValue(a: unknown, b: unknown, t: number): unknown {
  if (typeof a === 'number' && typeof b === 'number') return THREE.MathUtils.lerp(a, b, t);
  if (isHex(a) && isHex(b)) return lerpHex(a, b, t);
  if (isVec3(a) && isVec3(b)) {
    return {
      x: THREE.MathUtils.lerp(a.x, b.x, t),
      y: THREE.MathUtils.lerp(a.y, b.y, t),
      z: THREE.MathUtils.lerp(a.z, b.z, t),
    };
  }
  if (typeof a === 'boolean' && typeof b === 'boolean') return t < 0.5 ? a : b;
  return t < 0.5 ? clone(a) : clone(b);
}

function lerpAngleDeg(a: number, b: number, t: number): number {
  let delta = ((b - a + 540) % 360) - 180;
  if (delta === -180) delta = 180;
  return a + delta * t;
}

function isHex(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
}

function isVec3(value: unknown): value is { x: number; y: number; z: number } {
  return Boolean(value)
    && typeof value === 'object'
    && typeof (value as { x?: unknown }).x === 'number'
    && typeof (value as { y?: unknown }).y === 'number'
    && typeof (value as { z?: unknown }).z === 'number';
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
